// Data Lab · Enrichment Coverage — book-wide 360-readiness across customers.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RefreshCw, Loader2, ExternalLink, Download } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Panel } from "./_shared";

const BANDS = [
  { key: "all", label: "All" },
  { key: "none", label: "Not enriched" },
  { key: "partial", label: "Partial" },
  { key: "enriched", label: "Enriched" },
];

function Tile({ label, value, sub, tone = "slate", testid }) {
  const ring = { green: "border-emerald-500/30", warn: "border-amber-500/30", red: "border-rose-500/30", slate: "border-white/10" }[tone];
  return (
    <div className={`rounded-xl border ${ring} bg-[#111C34] p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-slate-100">{value}</div>
      {sub && <div className="text-[11px] font-mono text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function LabEnrichmentCoverage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [band, setBand] = useState("all");

  const load = useCallback(async (b) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/datalab/enrichment-coverage?band=${b}&limit=50`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load coverage");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(band); }, [band, load]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/datalab/enrichment-coverage/export?band=${band}&fmt=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `enrichment-coverage-${band}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Coverage CSV exported");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Export failed");
    }
  };

  const s = data?.summary || {};

  return (
    <div className="max-w-[1300px] mx-auto px-5 py-6 space-y-5" data-testid="lab-enrichment-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C8A46B]" />
            <h1 className="text-xl font-bold text-slate-100">Data Lab · Enrichment Coverage</h1>
          </div>
          <p className="text-[12px] text-slate-500 font-mono mt-1">
            360-readiness across the top {data?.working_set?.toLocaleString?.() || "—"} customers by lifetime value.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="lab-enrichment-export"
            className="inline-flex items-center gap-2 text-[12px] font-mono px-3 py-2 rounded-lg border border-[#C8A46B]/40 text-[#C8A46B] hover:bg-[#C8A46B]/10">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={() => load(band)} disabled={loading} data-testid="lab-enrichment-refresh"
            className="inline-flex items-center gap-2 text-[12px] font-mono px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-50">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Coverage" value={`${data?.coverage_pct ?? 0}%`} sub="≥1 CRM field" tone={(data?.coverage_pct ?? 0) >= 50 ? "green" : "red"} testid="cov-pct" />
        <Tile label="Not enriched" value={(s.none ?? 0).toLocaleString()} sub="0 CRM fields" tone="red" testid="cov-none" />
        <Tile label="Partial" value={(s.partial ?? 0).toLocaleString()} sub="1–7 fields" tone="warn" testid="cov-partial" />
        <Tile label="Enriched" value={(s.enriched ?? 0).toLocaleString()} sub="8+ fields" tone="green" testid="cov-enriched" />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {BANDS.map((b) => (
          <button key={b.key} onClick={() => setBand(b.key)} data-testid={`cov-band-${b.key}`}
            className={`text-[11px] font-mono px-2.5 py-1.5 rounded-lg border ${band === b.key ? "border-[#C8A46B]/50 bg-[#C8A46B]/10 text-[#C8A46B]" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
            {b.label}
          </button>
        ))}
        {data && <span className="text-[11px] font-mono text-slate-500 ml-1">{data.count?.toLocaleString()} match</span>}
      </div>

      <Panel icon={Sparkles} title="Customers" right={loading ? "…" : `${data?.items?.length || 0} shown`} testid="cov-table">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-left border-b border-white/10">
                  <th className="py-2 pr-3">Customer</th><th className="py-2 pr-3">VIP</th>
                  <th className="py-2 pr-3 text-right">LTV</th><th className="py-2 pr-3">CRM coverage</th>
                  <th className="py-2 pr-3">AI</th><th className="py-2 pr-3">Top gaps</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] align-top" data-testid={`cov-row-${r.id}`}>
                    <td className="py-2 pr-3">
                      <div className="text-slate-100 truncate max-w-[220px]">{r.name}</div>
                      {r.company && <div className="text-[10px] font-mono text-slate-500 truncate max-w-[220px]">{r.company}</div>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-slate-400">{r.vip || "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums text-slate-200">£{Number(r.ltv || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 rounded-full bg-white/[0.06] overflow-hidden">
                          <div className="h-full" style={{ width: `${Math.max(3, 100 * r.filled / r.total)}%`, background: r.filled >= 8 ? "#34d399" : r.filled >= 1 ? "#fbbf24" : "#f87171" }} />
                        </div>
                        <span className="font-mono text-[10px] text-slate-500 tabular-nums">{r.filled}/{r.total}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: r.ai ? "#34d399" : "#f87171" }} />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1 max-w-[280px]">
                        {r.missing.slice(0, 4).map((m) => (
                          <span key={m} className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300/80">{m}</span>
                        ))}
                        {r.missing.length > 4 && <span className="text-[9px] font-mono text-slate-500">+{r.missing.length - 4}</span>}
                      </div>
                    </td>
                    <td className="py-2 text-right">
                      <Link to={`/lab/persons/${r.id}`} className="inline-flex text-slate-500 hover:text-[#C8A46B]"><ExternalLink className="w-3.5 h-3.5" /></Link>
                    </td>
                  </tr>
                ))}
                {(data?.items || []).length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center text-slate-600">No customers in this band.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
